import { inject, Injectable } from '@angular/core';
import { _, TranslateService } from '@ngx-translate/core';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { MenuItem } from 'primeng/api';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { ldContextMenuTodo } from '@app/main/leak-detection/context-menus/utils/ld-context-menu-todo';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';
import { ContextMenuCopyService } from '@app/main/main-content/context-menu/services/context-menu-copy.service';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';

@Injectable({ providedIn: 'root' })
export class LdContextMenuCorrelationService {
  private readonly translateService = inject(TranslateService);
  private readonly contextMenuCopy = inject(ContextMenuCopyService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);

  public getMapItems(
    correlation: ICorrelation,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    return this.getCommonItems(correlation, LdActionPlace.Map, dispatch, viewMeasurementSystem);
  }

  public getTableItems(
    correlation: ICorrelation,
    cell: IMainTableCellContext | undefined,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    return [
      ...this.contextMenuCopy.getCopyCellItems(cell),
      ...this.getCommonItems(correlation, LdActionPlace.TabsPanel, dispatch, viewMeasurementSystem),
    ];
  }

  private getCommonItems(
    correlation: ICorrelation,
    place: LdActionPlace,
    dispatch: LdCoordinatorDispatch,
    viewMeasurementSystem: MeasurementSystem
  ): MenuItem[] {
    const nearbyEvents = this.contextMenuEvents.getNearbyEvents(correlation.coordinates);

    return [
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-correlation-details__label`)),
        icon: 'icons/icon-chart.svg',
        command: () => dispatch({ type: LdActionType.CorrelationDbClick, place, item: correlation }),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-manual-correlation__label`)),
        icon: 'icons/icon-drop.svg',
        command: () => ldContextMenuTodo('Manual correlation', correlation),
      },
      { separator: true },
      {
        ...this.contextMenuEvents.getCreateEventConfig(),
        command: () => ldContextMenuTodo('Create event', correlation),
      },
      this.contextMenuEvents.getEventDetailsMenuItem(nearbyEvents, viewMeasurementSystem, event =>
        dispatch({ type: LdActionType.EventDbClick, place, item: event })
      ),
      {
        ...this.contextMenuEvents.getAddToExistingEventConfig(),
        command: () => ldContextMenuTodo('Add to existing event', correlation),
      },
      {
        // no correlation-to-event relation in the data model yet — keeps the entry disabled
        ...this.contextMenuEvents.getRemoveFromEventConfig(undefined),
        command: () => ldContextMenuTodo('Remove from event', correlation),
      },
      { separator: true },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-show-in-street-view__label`)),
        icon: 'icons/icon-show-on-street.svg',
        iconStyle: { 'original-icon': true },
        command: () => dispatch({ type: LdActionType.ShowInStreetView, place, position: correlation.coordinates }),
      },
    ];
  }
}
